import { Table as TanstackTable, flexRender } from '@tanstack/react-table';
import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Table';

import { DataTablePagination } from './DataTablePagination';
import { DataTableToolbar } from './DataTableToolbar';
import { DataTableFilterField } from './types';
import { useTextHighlight } from './useTextHighlight';

export type ServerDataTableProps<TData extends Record<string, unknown>> = {
  table: TanstackTable<TData>;
  filterFields?: DataTableFilterField<TData>[];
  isPending?: boolean;
  placeholder?: string;
  toolbarLeftActions?: React.ReactNode;
  /**
   * When set, occurrences of this term in the rendered rows are highlighted
   * (parity with the client-side `DataTable`'s global-search highlight).
   */
  highlightTerm?: string;
};

export function ServerDataTable<TData extends Record<string, unknown>>({
  table,
  isPending,
  filterFields = [],
  placeholder,
  toolbarLeftActions,
  highlightTerm = '',
}: ServerDataTableProps<TData>) {
  const tableBodyRef = useTextHighlight(highlightTerm);

  return (
    <React.Fragment>
      <DataTableToolbar<TData>
        table={table}
        filterFields={filterFields}
        leftActions={toolbarLeftActions}
      />
      <Table
        className={`transition-opacity duration-200 ease-in-out ${isPending ? 'pointer-events-none opacity-50' : ''}`}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody ref={tableBodyRef}>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className="group"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={
                  table.getHeaderGroups()?.[0]?.headers?.length ??
                  table.getVisibleFlatColumns.length
                }
                className="h-24 text-center"
              >
                {placeholder ?? 'No results.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <DataTablePagination table={table} isPending={isPending} />
    </React.Fragment>
  );
}
